/**
 * Streaming Service - SSE-based real-time communication
 *
 * Uses SSE (Server-Sent Events) for real-time streaming:
 * - Chat messages via POST /message-stream (returns inline SSE)
 * - Other notifications via /events SSE (scheduler, delegations, etc.)
 * - Maintains compatibility with existing event bus architecture
 */

import { SSEStreamClient, transformSSEToIntermediateEvent, type SSEEvent } from '@/lib/sse-client.js';
import { eventBus } from '@/lib/events/index.js';
import { transformSSEIntermediateEvent, type SSEIntermediateEvent } from '@/lib/event-types.js';
import { useAgentStore } from '@/stores/agentStore.js';
import { useAskUserStore } from '@/stores/askUserStore.js';
import { useApprovalStore } from '@/stores/approvalStore.js';
import { getApiUrl } from '@/lib/api-url.js';

export class StreamingService {
  private sseClient: SSEStreamClient;
  private baseUrl: string;
  private _isConnected = false;

  constructor(
    _wsUrl: string, // Kept for backward compatibility, now ignored
    private getToken: () => string | null,
    private getUserId?: () => string | null
  ) {
    this.baseUrl = getApiUrl();
    this.sseClient = new SSEStreamClient(this.baseUrl);
  }

  /**
   * Connect - now just marks as "connected" since SSE is per-request
   * No persistent connection needed with SSE
   */
  async connect(): Promise<void> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No auth token available');
    }

    this._isConnected = true;

    // Dispatch connection status
    eventBus.dispatch({
      type: 'connectionStatus',
      status: 'connected',
      timestamp: Date.now(),
    });
  }

  /**
   * Send a message and stream the response via SSE
   */
  async sendMessageStream(
    message: string,
    sessionId: string,
    image?: { base64: string; mimeType: string },
    file?: { base64: string; mimeType: string; filename?: string }
  ): Promise<void> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No auth token available');
    }

    // Dispatch thinking status
    eventBus.dispatch({
      type: 'thinking',
      id: `thinking_${Date.now()}`,
      timestamp: Date.now(),
      sessionId,
    });

    // Get end-user ID for multi-tenant isolation
    const endUserId = this.getUserId?.() || undefined;

    await this.sseClient.streamMessage(
      message,
      sessionId,
      token,
      // Event handler
      (sseEvent: SSEEvent) => this.handleSSEEvent(sseEvent),
      // Error handler
      (error: Error) => {
        console.error('[StreamingService] SSE error:', error);
        eventBus.dispatch({
          type: 'error',
          id: `error_${Date.now()}`,
          timestamp: Date.now(),
          error: error.message,
          sessionId,
          recoverable: true,
        });
      },
      // Close handler
      () => {
        console.log('[StreamingService] SSE stream closed');
      },
      // Optional image/file data
      image,
      file,
      // End-user ID for multi-tenant isolation
      endUserId
    );
  }

  /**
   * Handle incoming SSE event
   * Transforms to intermediate format, then to AgentEvent
   */
  private handleSSEEvent(sseEvent: SSEEvent): void {
    // Log raw SSE event for debugging
    if (sseEvent.name.startsWith('approval')) {
      console.log('[StreamingService] Raw SSE approval event:', sseEvent.name, sseEvent.data);
    }

    // Transform SSE event to intermediate event format
    const intermediateData = transformSSEToIntermediateEvent(sseEvent);
    if (!intermediateData) {
      return;
    }

    const intermediateEvent = intermediateData as SSEIntermediateEvent;

    // Log transformed approval events
    if (intermediateEvent.type === 'approvalRequest' || intermediateEvent.type === 'approvalResolved') {
      console.log('[StreamingService] Transformed approval event:', intermediateEvent);
    }

    // Handle special cases that don't go through event bus
    switch (intermediateEvent.type) {
      case 'pong':
        useAgentStore.getState().updateHeartbeat();
        return;

      case 'askUserResolved':
        useAskUserStore.getState().setStatus((intermediateEvent as any).requestId, 'resolved');
        useAgentStore.getState().setAgentStatus('thinking');
        return;

      case 'mcpServerConnected':
      case 'availableToolsUpdated':
      case 'mcpResourceUpdated':
      case 'mcpPromptsListChanged':
      case 'mcpToolsListChanged':
      case 'resourceCacheInvalidated':
        console.debug(`[StreamingService] ${intermediateEvent.type}:`, intermediateEvent);
        return;
    }

    // Transform intermediate event to AgentEvent
    const agentEvent = transformSSEIntermediateEvent(intermediateEvent);

    // Log transformed agent event for approval types
    if (intermediateEvent.type === 'approvalRequest' || intermediateEvent.type === 'approvalResolved') {
      console.log('[StreamingService] Transformed to AgentEvent:', agentEvent);
    }

    // Dispatch to event bus
    if (agentEvent) {
      eventBus.dispatch(agentEvent);
    }
  }

  /**
   * Send generic message (legacy - for non-streaming operations use REST API)
   * @deprecated Use sendMessageStream for messages, REST API for other operations
   */
  send(_message: unknown): void {
    console.warn(
      '[StreamingService] send() is deprecated. Use sendMessageStream() for messages or REST API for other operations.'
    );
  }

  /**
   * Disconnect - cancel any active streams
   */
  disconnect(): void {
    this.sseClient.cancel();
    this._isConnected = false;

    eventBus.dispatch({
      type: 'connectionStatus',
      status: 'disconnected',
      timestamp: Date.now(),
    });
  }

  /**
   * Check if connected (always true when authenticated for SSE)
   */
  isConnected(): boolean {
    return this._isConnected;
  }

  /**
   * Update auth token
   */
  updateToken(_token: string | null): void {
    // SSE doesn't need reconnection - each request carries token
    // Just update connected state if token is removed
    if (!_token) {
      this._isConnected = false;
    }
  }

  /**
   * Build headers with optional X-End-User-Id for multi-tenant isolation
   */
  private buildHeaders(): Record<string, string> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const endUserId = this.getUserId?.();
    if (endUserId) {
      headers['X-End-User-Id'] = endUserId;
    }
    return headers;
  }

  /**
   * Send ask_user response via REST API
   */
  async sendAskUserResponse(
    requestId: string,
    responses: Record<string, unknown>
  ): Promise<void> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No auth token available');
    }

    // Ask-user responses go through the approval endpoint with formData
    const response = await fetch(`${this.baseUrl}/approvals/${requestId}`, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify({
        status: 'approved',
        formData: responses,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to submit response: ${errorText}`);
    }

    // Update local store
    const store = useAskUserStore.getState();
    store.setResponses(requestId, responses);
    store.setStatus(requestId, 'submitted');
    useAgentStore.getState().setAgentStatus('thinking');
  }

  /**
   * Approve a request via REST API
   */
  async approveRequest(approvalId: string): Promise<void> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No auth token available');
    }

    const response = await fetch(`${this.baseUrl}/approvals/${approvalId}`, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify({
        status: 'approved',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to approve: ${errorText}`);
    }

    useApprovalStore.getState().updateApprovalStatus(approvalId, 'approved');
    useAgentStore.getState().setAgentStatus('thinking');
  }

  /**
   * Reject a request via REST API
   */
  async rejectRequest(approvalId: string): Promise<void> {
    const token = this.getToken();
    if (!token) {
      throw new Error('No auth token available');
    }

    const response = await fetch(`${this.baseUrl}/approvals/${approvalId}`, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify({
        status: 'denied',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to reject: ${errorText}`);
    }

    useApprovalStore.getState().updateApprovalStatus(approvalId, 'rejected');
    useAgentStore.getState().setAgentStatus('idle');
  }

  /**
   * Cancel current stream
   */
  cancelStream(): void {
    this.sseClient.cancel();
  }
}
